import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { mergeCheckins } from "@/lib/wellnessAdapter";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, BarChart, Bar, Legend } from "recharts";
import { Activity, Dumbbell, Target, GripVertical } from "lucide-react";
import { METRIC_COLOR, WELLNESS_PALETTE, chartTooltipStyle } from "@/lib/metricColors";
import { Skeleton } from "@/components/Skeleton";
import SortableGrid from "@/components/SortableGrid";
import { useDashboardLayout } from "@/hooks/useDashboardLayout";
import { useEditMode } from "@/lib/editMode";
import UniversalResultsPatient from "@/components/UniversalResultsPatient";

// friendly test-type labels for device readings
const TEST_LABELS = {
  quad_iso_max: "Quad isometric max",
  ham_iso_max: "Hamstring isometric max",
  calf_iso_max: "Calf isometric max",
  cmj_height: "Countermovement jump height",
  sj_peak_force: "Squat jump peak force",
  balance_sway: "Balance sway",
};

const rtsPct = (v) => (v == null ? null : Math.round(v <= 1.5 ? v * 100 : v));

// iter99 · patient-side Baseline vs Current graphic.
function BaselineCurrentChart({ summary }) {
  const rows = useMemo(() => {
    const b = summary?.baseline, c = summary?.current;
    if (!b) return [];
    const acc = {};
    const categoriseFn = (item) => {
      const name = (item.motion || item.test || item.label || item.name || "").toLowerCase();
      if ((item.category || "").toLowerCase().includes("balance") || /balance|y[- ]balance|sway/i.test(name)) return "Balance";
      if (/sprint|10m|20m|40y|speed|dash/i.test(name)) return "Speed";
      if (/jump|cmj|sj|hop|drop|power/i.test(name)) return "Power";
      return null;
    };
    const push = (cat, label, side, baseVal, curVal) => {
      const key = `${cat}::${label}${side && side !== "bilateral" ? " ("+side+")" : ""}`;
      if (!acc[key]) acc[key] = { category: cat, test: `${label}${side && side !== "bilateral" ? " ("+side+")" : ""}`, baseline: null, current: null };
      if (baseVal != null && !Number.isNaN(+baseVal)) acc[key].baseline = +baseVal;
      if (curVal != null && !Number.isNaN(+curVal)) acc[key].current = +curVal;
    };
    const walk = (doc, curDoc) => {
      (doc?.rom || []).forEach((r) => {
        const match = (curDoc?.rom || []).find((x) => x.joint === r.joint && x.motion === r.motion && (x.side || "bilateral") === (r.side || "bilateral"));
        push("ROM", `${r.joint || "Joint"} ${r.motion || ""}`.trim(), r.side, r.value ?? r.degrees, match?.value ?? match?.degrees);
      });
      (doc?.strength || []).forEach((s) => {
        const match = (curDoc?.strength || []).find((x) => (x.muscle || x.test) === (s.muscle || s.test) && (x.side || "bilateral") === (s.side || "bilateral"));
        push("Strength", s.muscle || s.test || s.name || "Strength", s.side, s.value ?? s.grade ?? s.force, match?.value ?? match?.grade ?? match?.force);
      });
      (doc?.functional || []).forEach((f) => {
        const label = f.name || f.test || f.label || "Functional";
        const cat = categoriseFn(f) || "Power";
        const match = (curDoc?.functional || []).find((x) => (x.name || x.test || x.label) === label && (x.side || "bilateral") === (f.side || "bilateral"));
        push(cat, label, f.side, f.value ?? f.score ?? f.result, match?.value ?? match?.score ?? match?.result);
      });
    };
    walk(b, c);
    return Object.values(acc);
  }, [summary]);

  if (!summary?.baseline) {
    return (
      <div className="fl-card fl-card-elevated p-6" data-testid="baseline-current-empty">
        <div className="fl-heading text-xl mb-2">Baseline vs current</div>
        <div className="text-sm text-zinc-500">Your clinician hasn't completed a baseline assessment yet — once they do, we'll chart your progress here.</div>
      </div>
    );
  }
  if (!summary?.current) {
    return (
      <div className="fl-card fl-card-elevated p-6" data-testid="baseline-current-single">
        <div className="fl-heading text-xl mb-2">Baseline vs current</div>
        <div className="text-sm text-zinc-500">Only a baseline is on file so far — we'll show a comparison once your next reassessment is complete.</div>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="fl-card fl-card-elevated p-6" data-testid="baseline-current-no-tests">
        <div className="fl-heading text-xl mb-2">Baseline vs current</div>
        <div className="text-sm text-zinc-500">No structured measurements in your assessments yet — ROM / strength / functional numbers will appear here after your next in-clinic session.</div>
      </div>
    );
  }

  const CATS = ["ROM", "Strength", "Balance", "Power", "Speed"];
  const byCat = CATS.map((cat) => ({ cat, items: rows.filter((r) => r.category === cat) })).filter((x) => x.items.length);
  return (
    <div className="fl-card fl-card-elevated p-6" data-testid="baseline-current">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="fl-heading text-xl">Baseline vs current</div>
        <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
          Baseline · {new Date(summary.baseline.created_at).toLocaleDateString("en-GB")} &nbsp; · &nbsp;
          Current · {new Date(summary.current.created_at).toLocaleDateString("en-GB")}
        </div>
      </div>
      <div className="space-y-6">
        {byCat.map(({ cat, items }) => (
          <div key={cat} data-testid={`bc-${cat.toLowerCase()}`}>
            <div className="fl-label mb-2">{cat}</div>
            <ResponsiveContainer width="100%" height={Math.max(160, items.length * 40 + 60)}>
              <BarChart data={items} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                <XAxis type="number" stroke="#71717A" fontSize={11} />
                <YAxis type="category" dataKey="test" stroke="#71717A" fontSize={11} width={140} />
                <Tooltip {...chartTooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="baseline" name="Baseline" fill="#71717A" radius={[0, 3, 3, 0]} />
                <Bar dataKey="current"  name="Current"  fill={METRIC_COLOR.readiness} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
      <div className="text-[11px] mt-4" style={{ color: "var(--fl-muted-2)" }}>
        Raw numbers only — clinician commentary stays in your assessment record, not on your progress page.
      </div>
    </div>
  );
}

// iter111.2 · Title/eyebrow reflects the selected metrics view (was hard-
// coded to Progress before, which mismatched Physical Wellness/Tests URLs
// and confused screen readers).
const VIEW_META = {
  progress: { eyebrow: "Metrics",           h1: "Progress" },
  wellness: { eyebrow: "Metrics",           h1: "Physical Wellness" },
  tests:    { eyebrow: "Metrics",           h1: "Physical Tests" },
};

export default function PatientProgress() {
  const { t } = useTranslation();
  const [checkins, setCheckins] = useState([]);
  const [coachCheckins, setCoachCheckins] = useState([]);
  const [logs, setLogs] = useState([]);
  const [rts, setRts] = useState([]);
  const [readings, setReadings] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [assessSummary, setAssessSummary] = useState(null);
  const loc = useLocation();
  const nav = useNavigate();
  const _view = (() => {
    const s = new URLSearchParams(loc.search).get("section");
    return s === "wellness" || s === "tests" ? s : "progress";
  })();
  const setView = (v) => nav(`/app/progress${v === "progress" ? "" : `?section=${v}`}`, { replace: false });
  const [loading, setLoading] = useState(true);
  const { editing: editLayout } = useEditMode();

  // iter111.2 · Sessions Completed moved out of wellness; wellness only owns
  // the wellness_trend widget now. Any saved layouts that still list
  // `sessions_completed` under wellness are silently dropped in the render
  // switch — we don't rewrite the server key so the migration is reversible
  // if we ever bring it back.
  const {
    visibleOrder: wellnessOrder,
    setOrder: setWellnessOrder,
    toggleHidden: hideWellness,
    resetToDefault: resetWellness,
    hidden: wellnessHidden,
    isDirty: wellnessDirty,
  } = useDashboardLayout("patient_progress_wellness", ["wellness_trend"]);
  const {
    visibleOrder: sectionOrder,
    setOrder: setSectionOrder,
    setSize: sizeSection,
    sizes: sectionSizes,
  } = useDashboardLayout("patient_progress_sections", ["milestones", "feedback"]);

  useEffect(() => {
    Promise.all([
      api.get("/checkins/mine"),
      api.get("/coach/checkins/mine").catch(() => ({ data: [] })),
      api.get("/exercise-logs/mine"),
      api.get("/rts-tests/mine").catch(() => ({ data: [] })),
      api.get("/device-readings/mine").catch(() => ({ data: [] })),
      api.get("/sessions/mine").catch(() => ({ data: [] })),
      api.get("/assessments/mine/summary").catch(() => ({ data: null })),
    ]).then(([c, cc, l, r, d, s, a]) => {
      setCheckins(c.data); setCoachCheckins(cc.data || []); setLogs(l.data); setRts(r.data); setReadings(d.data); setSessions(s.data);
      setAssessSummary(a.data);
    }).finally(() => setLoading(false));
  }, []);

  // ─── Wellness trend ──
  //  • Range control (7d / 30d / all) — frontend-only filter over the
  //    same wellness-adapter output. All ISO dates flow through unchanged.
  //  • Elapsed-time X axis via `type="number" scale="time" dataKey="_ts"` so
  //    a two-week gap looks like a two-week gap. Missing days are gaps —
  //    we never synthesise records.
  //  • Sleep quality kept on its native 1–5 scale via a right axis.
  const [wellnessRange, setWellnessRange] = useState("30d");
  // iter111.4 · Custom accessible legend chips toggle each series.
  const [hidden, setHidden] = useState({});
  const wellnessMerged = useMemo(() => mergeCheckins(checkins, coachCheckins), [checkins, coachCheckins]);
  const wellnessCutoff = useMemo(() => {
    if (wellnessRange === "all") return null;
    const days = wellnessRange === "7d" ? 7 : 30;
    return Date.now() - days * 86_400_000;
  }, [wellnessRange]);
  const chartData = useMemo(() => (
    wellnessMerged
      .filter((c) => {
        if (!wellnessCutoff) return true;
        const t = new Date(c.date + "T00:00:00").getTime();
        return t >= wellnessCutoff;
      })
      .map((c) => ({
        _iso: c.date,
        _ts:  new Date(c.date + "T00:00:00").getTime(),
        pain: c.pain,
        fatigue: c.fatigue,
        sleep_quality: c.sleep_quality,
        confidence: c.confidence,
      }))
  ), [wellnessMerged, wellnessCutoff]);
  // How many x-ticks fit the current width without overlapping. Recharts
  // spaces them evenly but we constrain the total.
  const wellnessTickCount = chartData.length <= 5 ? chartData.length : chartData.length <= 10 ? 5 : 6;

  // ─── Adherence / sessions completed ──
  // iter111.2 bug: `Object.entries(byDay)` returned keys in log insertion
  // order (often newest first from the API), so the chart labelled September
  // *before* August. Sort by ISO date string BEFORE formatting the display
  // labels, and keep the ISO alongside for downstream consumers.
  const adhData = useMemo(() => {
    const byDay = {};
    logs.forEach((l) => {
      const iso = (l.created_at || "").slice(0, 10);
      if (iso) byDay[iso] = (byDay[iso] || 0) + 1;
    });
    return Object.entries(byDay)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-14)
      .map(([iso, count]) => ({
        _iso: iso,
        date: new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
        sessions: count,
      }));
  }, [logs]);

  const rtsChart = useMemo(() => {
    // Sort ascending by ISO created_at rather than relying on server order.
    return [...rts]
      .filter((t) => t.created_at)
      .sort((a, b) => a.created_at.localeCompare(b.created_at))
      .map((t) => ({
        _iso: t.created_at,
        date: new Date(t.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
        quad_lsi: rtsPct(t.quad_lsi),
        hop_lsi: rtsPct(t.single_hop_lsi),
        triple_lsi: rtsPct(t.triple_hop_lsi),
        cmj: t.cmj_height_cm,
        psych: t.psych_readiness,
      }));
  }, [rts]);

  const readingsChart = useMemo(() => {
    const grouped = {};
    [...readings]
      .filter((r) => r.performed_at || r.created_at)
      .sort((a, b) => (a.performed_at || a.created_at).localeCompare(b.performed_at || b.created_at))
      .forEach((r) => {
        const iso = (r.performed_at || r.created_at).slice(0, 10);
        const label = new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
        if (!grouped[iso]) grouped[iso] = { _iso: iso, date: label };
        const key = `${r.test_type}${r.side && r.side !== "bilateral" ? "_" + r.side : ""}`;
        grouped[iso][key] = r.value;
      });
    return Object.values(grouped).sort((a, b) => a._iso.localeCompare(b._iso));
  }, [readings]);

  const readingKeys = useMemo(() => {
    const s = new Set();
    readings.forEach((r) => s.add(`${r.test_type}${r.side && r.side !== "bilateral" ? "_" + r.side : ""}`));
    return Array.from(s);
  }, [readings]);
  const COLORS = ["#f47a20", "#22C55E", "#3B82F6", "#F59E0B", "#EF4444", "#A855F7", "#14B8A6"];

  const feedbackCount = { easy: 0, ok: 0, hard: 0 };
  sessions.forEach((s) => { if (s.feedback in feedbackCount) feedbackCount[s.feedback]++; });

  const meta = VIEW_META[_view] || VIEW_META.progress;

  return (
    <DashLayout>
      <h1 className="sr-only" data-testid="metrics-h1">{meta.h1}</h1>

      {/* Mobile section switcher — the desktop binder sub-tabs are hidden on
          small screens, so Progress/Wellness/Tests are reached from here. */}
      <div className="md:hidden flex items-center gap-1.5 overflow-x-auto no-scrollbar mb-4" data-testid="progress-mobile-sections">
        {[
          ["progress", VIEW_META.progress.h1, "nav-progress-sub"],
          ["wellness", VIEW_META.wellness.h1, "nav-physical-wellness"],
          ["tests", VIEW_META.tests.h1, "nav-physical-tests"],
        ].map(([key, label, tid]) => {
          const active = _view === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              data-testid={`m-${tid}`}
              data-active={active ? "true" : "false"}
              className="shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors"
              style={{
                color: active ? "#fff" : "var(--fl-muted)",
                background: active ? "color-mix(in oklab, #3B82F6 18%, transparent)" : "transparent",
                border: "1px solid var(--fl-border)",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="progress-loading">
          <div className="fl-card fl-card-elevated p-6">
            <Skeleton className="h-5 w-56 mb-4" />
            <Skeleton className="h-[260px] w-full rounded-lg" />
          </div>
          <div className="fl-card fl-card-elevated p-6">
            <Skeleton className="h-5 w-40 mb-4" />
            <Skeleton className="h-[260px] w-full rounded-lg" />
          </div>
        </div>
      )}

      {/* ─── PHYSICAL WELLNESS ─────────────────────────────────────── */}
      {!loading && _view === "wellness" && (
        <div data-testid="metrics-wellness-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="fl-label text-xs text-zinc-500">Your wellness widgets</div>
            <div className="flex items-center gap-2">
              {editLayout && wellnessDirty && (
                <button type="button" onClick={resetWellness}
                        className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white"
                        data-testid="reset-layout">Reset to default</button>
              )}
              {editLayout && wellnessHidden.size > 0 && (
                <div className="flex items-center gap-1 text-[10px] text-zinc-400" data-testid="hidden-chips">
                  <span className="uppercase tracking-widest">Hidden:</span>
                  {[...wellnessHidden].map((id) => (
                    <button key={id} type="button" onClick={() => hideWellness(id)}
                            className="px-2 py-0.5 rounded border border-white/10 hover:border-[color:var(--fl-orange)]"
                            data-testid={`unhide-${id}`}>+ {id}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/*
            iter111.2 · `rowHeight="auto"` lets wellness cards grow to
            fit their entire chart + legend + source footnote. The 200 px
            fixed height stays scoped to Today via the SortableGrid default.
          */}
          <SortableGrid
            items={wellnessOrder}
            onReorder={setWellnessOrder}
            onHide={hideWellness}
            editMode={editLayout}
            rowHeight="auto"
            className="grid grid-cols-1 gap-4 mb-6"
          >
            {wellnessOrder.map((id) => {
              if (id === "wellness_trend") {
                const fmtDate = (ts) => new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
                return (
                  <SortableGrid.Item key="wellness_trend" id="wellness_trend">
                    <div className="fl-card fl-card-elevated p-6 h-full flex flex-col" data-testid="widget-wellness_trend">
                      <div className="flex items-center justify-between flex-wrap gap-3 mb-2 shrink-0">
                        <div className="fl-heading text-xl">Wellness trend</div>
                        <div role="group" aria-label="Date range" className="inline-flex rounded-md overflow-hidden border border-white/10" data-testid="wellness-range">
                          {[["7d", "7 days"], ["30d", "30 days"], ["all", "All"]].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setWellnessRange(k)}
                              className={`px-3 py-1 text-[11px] uppercase tracking-widest transition-colors ${wellnessRange === k ? "bg-[#f47a20] text-black font-semibold" : "text-zinc-400 hover:text-white"}`}
                              data-testid={`wellness-range-${k}`}>{l}</button>
                          ))}
                        </div>
                      </div>
                      {/* Captions above the plot so we never need vertical axis titles */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] mb-2 shrink-0" style={{ color: "var(--fl-muted-2)" }}>
                        <span>Pain, fatigue, confidence: <b style={{ color: "var(--fl-text)" }}>0–10</b></span>
                        <span>Sleep quality: <b style={{ color: WELLNESS_PALETTE.sleep_quality }}>1–5</b></span>
                      </div>
                      {chartData.length ? (
                        <div className="w-full" style={{ minHeight: 320 }} data-testid="widget-wellness_trend-chart">
                          <ResponsiveContainer width="100%" height={320}>
                            <LineChart data={chartData} margin={{ top: 6, right: 16, left: 4, bottom: 8 }}>
                              <CartesianGrid stroke="var(--fl-border)" strokeDasharray="3 3" opacity={0.4} />
                              <XAxis
                                type="number"
                                dataKey="_ts"
                                scale="time"
                                domain={["dataMin", "dataMax"]}
                                stroke="var(--fl-muted-2)"
                                fontSize={11}
                                tickCount={wellnessTickCount}
                                tickFormatter={fmtDate}
                                tickMargin={6}
                              />
                              <YAxis yAxisId="ten" orientation="left"  stroke="var(--fl-muted-2)"          fontSize={11} domain={[0, 10]} width={30} tickCount={6} allowDecimals={false} />
                              <YAxis yAxisId="five" orientation="right" stroke={WELLNESS_PALETTE.sleep_quality} fontSize={11} domain={[1, 5]}  width={30} tickCount={5} allowDecimals={false} />
                              <Tooltip
                                {...chartTooltipStyle()}
                                labelFormatter={(ts) => new Date(ts).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}
                                formatter={(v, name) => {
                                  if (v == null) return ["No recording", name];
                                  if (name === "Sleep quality") return [`${v} / 5`, name];
                                  return [`${v} / 10`, name];
                                }}
                              />
                              <Legend
                                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                                iconType="plainline"
                                // iter111.4 · Accessible show/hide legend
                                // chips. Recharts wires clicks to
                                // handleClick; we also add role/aria/tabIndex
                                // + Enter/Space keys via the wrapper.
                                onClick={(entry) => setHidden((prev) => {
                                  const next = { ...prev }; next[entry.dataKey] = !next[entry.dataKey]; return next;
                                })}
                                content={(props) => (
                                  <ul className="flex flex-wrap gap-3 items-center justify-center pt-3" role="group" aria-label="Toggle wellness series">
                                    {(props.payload || []).map((entry) => {
                                      const off = !!hidden[entry.dataKey];
                                      return (
                                        <li key={entry.dataKey}>
                                          <button
                                            type="button"
                                            role="switch"
                                            aria-checked={!off}
                                            tabIndex={0}
                                            onClick={() => setHidden((prev) => ({ ...prev, [entry.dataKey]: !prev[entry.dataKey] }))}
                                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setHidden((prev) => ({ ...prev, [entry.dataKey]: !prev[entry.dataKey] })); } }}
                                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] transition border focus:outline-none focus:ring-2 focus:ring-[color:var(--fl-orange)]"
                                            style={{
                                              color: off ? "var(--fl-muted-2)" : entry.color,
                                              borderColor: off ? "var(--fl-border)" : entry.color,
                                              opacity: off ? 0.55 : 1,
                                              background: "transparent",
                                            }}
                                            data-testid={`wellness-legend-${entry.dataKey}`}
                                          >
                                            <span aria-hidden="true" style={{ width: 10, height: 2, background: entry.color, display: "inline-block", borderRadius: 1 }} />
                                            {entry.value}
                                          </button>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              />
                              <Line yAxisId="ten"  type="linear" dataKey="pain"          name="Pain"          stroke={WELLNESS_PALETTE.pain}          strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} hide={!!hidden.pain} />
                              <Line yAxisId="ten"  type="linear" dataKey="fatigue"       name="Fatigue"       stroke={WELLNESS_PALETTE.fatigue}       strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} hide={!!hidden.fatigue} />
                              <Line yAxisId="ten"  type="linear" dataKey="confidence"    name="Confidence"    stroke={WELLNESS_PALETTE.confidence}    strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} hide={!!hidden.confidence} />
                              <Line yAxisId="five" type="linear" dataKey="sleep_quality" name="Sleep quality" stroke={WELLNESS_PALETTE.sleep_quality} strokeWidth={2} dot={{ r: 4, strokeWidth: 1, fill: "#000" }} activeDot={{ r: 6 }} connectNulls={false} strokeDasharray="5 4" hide={!!hidden.sleep_quality} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="text-zinc-500 text-sm py-8" data-testid="wellness-empty">No recordings in this period.</div>
                      )}
                      <div className="text-[11px] mt-3 shrink-0 space-y-1" style={{ color: "var(--fl-muted-2)" }}>
                        <div>Sourced from your Daily Check-in. Fatigue uses directly recorded values only; missing dates are shown as gaps.</div>
                        <div>Lower <b>pain</b>/<b>fatigue</b> = less reported. Higher <b>confidence</b>/<b>sleep quality</b> = greater confidence / better sleep.</div>
                      </div>
                    </div>
                  </SortableGrid.Item>
                );
              }
              return null;
            })}
          </SortableGrid>
        </div>
      )}

      {/* ─── PROGRESS ──────────────────────────────────────────────── */}
      {!loading && _view === "progress" && (
        <div data-testid="metrics-progress-panel">
          <SortableGrid
            items={sectionOrder}
            onReorder={setSectionOrder}
            onResize={sizeSection}
            sizes={{ milestones: "2x1", feedback: "2x1", ...sectionSizes }}
            editMode={editLayout}
            rowHeight="auto"
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6"
          >
            <SortableGrid.Item key="milestones" id="milestones">
              <div className="fl-card fl-card-elevated p-6 h-full">
                <div className="fl-heading text-xl mb-4">Milestones</div>
                <div className="grid md:grid-cols-4 gap-4">
                  {[
                    ["Total sessions", sessions.length || logs.length],
                    ["Check-ins", checkins.length],
                    ["Programme days", Math.max(0, checkins.length)],
                    ["Physical tests", rts.length + readings.length],
                  ].map(([l, v]) => (
                    <div key={l}>
                      <div className="fl-label">{l}</div>
                      <div className="fl-heading text-4xl text-[#f47a20] mt-1">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </SortableGrid.Item>

            {sessions.length > 0 && (
              <SortableGrid.Item key="feedback" id="feedback">
                <div className="fl-card fl-card-elevated p-6 h-full">
                  <div className="fl-heading text-xl mb-4">Session feedback breakdown</div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><div className="fl-label">😌 Too easy</div><div className="fl-heading text-3xl mt-1">{feedbackCount.easy}</div></div>
                    <div><div className="fl-label">✅ Just right</div><div className="fl-heading text-3xl mt-1 text-[#f47a20]">{feedbackCount.ok}</div></div>
                    <div><div className="fl-label">😓 Too hard</div><div className="fl-heading text-3xl mt-1">{feedbackCount.hard}</div></div>
                  </div>
                </div>
              </SortableGrid.Item>
            )}
          </SortableGrid>

          {/* Sessions Completed + adherence — belongs in Progress, not
              Physical Wellness. Data is the same underlying `adhData`
              stream (no duplicate chart is rendered elsewhere). */}
          <div className="fl-card fl-card-elevated p-6" data-testid="metrics-progress-adherence">
            <div className="fl-heading text-xl mb-4">Sessions completed · adherence</div>
            {adhData.length ? (
              <div className="w-full" style={{ minHeight: 260 }}>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={adhData} margin={{ top: 4, right: 12, left: 0, bottom: 4 }}>
                    <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                    <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickMargin={4} />
                    <YAxis stroke="#71717A" fontSize={11} allowDecimals={false} width={28} />
                    <Tooltip {...chartTooltipStyle()} />
                    <Bar dataKey="sessions" name="Sessions" fill={METRIC_COLOR.adherence} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="text-zinc-500 text-sm">Log exercises to see sessions.</div>}
          </div>
        </div>
      )}

      {/* ─── PHYSICAL TESTS ────────────────────────────────────────── */}
      {!loading && _view === "tests" && (
        <div className="space-y-6" data-testid="physical-tests-panel">
          <UniversalResultsPatient />
          <BaselineCurrentChart summary={assessSummary} />

          <div className="fl-card fl-card-elevated p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="fl-heading text-xl flex items-center gap-2"><Target size={18} /> Return-to-Sport battery</div>
              <div className="fl-label">{rts.length} test session{rts.length === 1 ? "" : "s"}</div>
            </div>
            {rts.length ? (
              <>
                <div className="w-full" style={{ minHeight: 260 }}>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={rtsChart} margin={{ top: 4, right: 12, left: 0, bottom: 4 }}>
                      <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                      <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickMargin={4} />
                      <YAxis stroke="#71717A" fontSize={11} domain={[0, 110]} width={40} label={{ value: "LSI %", angle: -90, position: "insideLeft", fill: "#71717A", fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: "#141414", border: "1px solid #333", borderRadius: 4 }} />
                      <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
                      <Line dataKey="quad_lsi" name="Quad LSI" stroke="#f47a20" strokeWidth={2} />
                      <Line dataKey="hop_lsi" name="Single-hop LSI" stroke="#22C55E" strokeWidth={2} />
                      <Line dataKey="triple_lsi" name="Triple-hop LSI" stroke="#3B82F6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left fl-label border-b border-white/10">
                        <th className="py-2">Date</th>
                        <th className="py-2">Quad LSI</th>
                        <th className="py-2">Hop LSI</th>
                        <th className="py-2">Triple-hop LSI</th>
                        <th className="py-2">CMJ (cm)</th>
                        <th className="py-2">Psych</th>
                        <th className="py-2">Ready</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {rts.map((t) => (
                        <tr key={t.id} className="text-zinc-300">
                          <td className="py-2">{new Date(t.created_at).toLocaleDateString("en-GB")}</td>
                          <td className="py-2">{rtsPct(t.quad_lsi) != null ? `${rtsPct(t.quad_lsi)}%` : "—"}</td>
                          <td className="py-2">{rtsPct(t.single_hop_lsi) != null ? `${rtsPct(t.single_hop_lsi)}%` : "—"}</td>
                          <td className="py-2">{rtsPct(t.triple_hop_lsi) != null ? `${rtsPct(t.triple_hop_lsi)}%` : "—"}</td>
                          <td className="py-2">{t.cmj_height_cm ?? "—"}</td>
                          <td className="py-2">{t.psych_readiness ?? "—"}/100</td>
                          <td className="py-2">
                            {t.overall_ready
                              ? <span className="text-[#22C55E]">✓ Ready</span>
                              : <span className="text-zinc-500">In progress</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : <div className="text-zinc-500 text-sm">No RTS test sessions yet. Your clinician will schedule these as you progress.</div>}
          </div>

          <div className="fl-card fl-card-elevated p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="fl-heading text-xl flex items-center gap-2"><Dumbbell size={18} /> Strength & performance tests</div>
              <div className="fl-label">{readings.length} reading{readings.length === 1 ? "" : "s"}</div>
            </div>
            {readings.length ? (
              <>
                <div className="w-full" style={{ minHeight: 260 }}>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={readingsChart} margin={{ top: 4, right: 12, left: 0, bottom: 4 }}>
                      <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                      <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickMargin={4} />
                      <YAxis stroke="#71717A" fontSize={11} width={40} />
                      <Tooltip contentStyle={{ background: "#141414", border: "1px solid #333", borderRadius: 4 }} />
                      <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
                      {readingKeys.map((k, i) => (
                        <Line key={k} dataKey={k} stroke={COLORS[i % COLORS.length]} strokeWidth={2} connectNulls />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left fl-label border-b border-white/10">
                        <th className="py-2">Date</th>
                        <th className="py-2">Test</th>
                        <th className="py-2">Side</th>
                        <th className="py-2">Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {readings.slice(0, 30).map((r) => (
                        <tr key={r.id} className="text-zinc-300">
                          <td className="py-2">{new Date(r.performed_at || r.created_at).toLocaleDateString("en-GB")}</td>
                          <td className="py-2">{TEST_LABELS[r.test_type] || r.test_type}</td>
                          <td className="py-2 capitalize">{r.side || "—"}</td>
                          <td className="py-2 font-mono">{r.value} {r.units}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : <div className="text-zinc-500 text-sm">No strength readings yet. Your clinician records these on the in-clinic Activforce dynamometer and force plate.</div>}
          </div>
        </div>
      )}
    </DashLayout>
  );
}
